// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

 // NOTE - 'platform' is a peerDependency here, please ensure it is present in this module's client's node_modules

import platform from 'platform';

class DeviceDetectionUtil {
	isWin() {
		if (platform && platform.os && platform.os.family) {
			return platform.os.family.toLowerCase().includes('windows');
		}
		return navigator.appVersion.indexOf('Win') !== -1;
	}

	isLinux() {
		if (platform && platform.os && platform.os.family) {
			return platform.os.family.toLowerCase() === 'linux' || platform.os.family.toLowerCase() === 'ubuntu';
		}
		return false;
	}

	isMobileOrTab() {
		// Comment from downloads pages - Show an error when someone on a touch device tries to download a
		// desktop-only product. Some Windows devices are touch devices, but
		// can still run our products, so don't show error for them. However,
		// Windows RT devices are ARM-based and cannot run our products, so do
		// show the alert for them.

		const isNonWinOrWinRT = (!this.isWin() || this.isARM());
		return (isNonWinOrWinRT && !this.isLinux() && this.isTouchDevice()) || this.isChromeOS();
	}

	/**
	 * returns true if device is touch based
	 *
	 * @returns true or false
	 */
	isTouchDevice() {
		return (
			!!(typeof window !== 'undefined' && ('ontouchstart' in window || (window.DocumentTouch && typeof document !== 'undefined' && document instanceof window.DocumentTouch))) || !!(typeof navigator !== 'undefined' && (window.navigator.maxTouchPoints || window.navigator.msMaxTouchPoints))
		);
	}

	/**
	 * returns true if device is ARM based
	 *
	 * @returns true or false
	 */
	isARM() {
		const agent = navigator.userAgent.toLowerCase();
		const arm = agent.match(('arm;').toLowerCase());
		return (arm && arm.length > 0);
	}

	/**
	 * returns true if device is Chrome OS based. Eg: a chromebook
	 *
	 * @returns true or false
	 */
	isChromeOS() {
		return navigator.userAgent.indexOf('CrOS') !== -1;
	}

	isSafari() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('safari');
		}
		return false;
	}

	isFirefox() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('firefox');
		}
		return false;
	}

	isEdgeOrIE() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('microsoft edge') || platform.name.toLowerCase().includes('ie');
		}
		return false;
	}

	isEdge() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('edge') || navigator.userAgent.search(/\bedg\//i) !== -1;
		}
		return false;
	}

	isChrome() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('chrome') && !(navigator.userAgent.search(/\bedg\//i) !== -1);
		}
		return false;
	}

	isEdgeBeta() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('chrome') && (navigator.userAgent.search(/\bedg\//i) !== -1);
		}
		return false;
	}

	isInternetExplorer() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('ie');
		}
		return false;
	}

	isIPad() {
		if (platform && platform.os && platform.os.family && platform.os.family.toLowerCase() === 'ios'
			&& platform.product && platform.product.toLowerCase() === 'ipad') {
			return true;
		}
		// detect iPad when default desktop setting is on
		return (this.isMacOS()
			&& this.isSafari()
			&& !this.isIPhone()
			&& this.isTouchDevice());
	}

	isIPadOS() {
		if (platform
			&& platform.os
			&& platform.os.family
			&& platform.os.family.toLowerCase() === 'ios'
			&& platform.product
			&& platform.product.toLowerCase() === 'ipad') {
			return true;
		}
		// detect iPad when default desktop setting is on
		let width = screen.availWidth;
		if (screen.availHeight < screen.availWidth) {
			width = screen.availHeight;
		}
		return (this.isMacOS()
			&& this.isTouchDevice()
			&& (((width * devicePixelRatio) >= 1300) || ((width * devicePixelRatio) === 768)));
	}

	isIPhone() {
		return platform && platform.os && platform.os.family && platform.os.family.toLowerCase() === 'ios'
			&& platform.product && platform.product.toLowerCase() === 'iphone';
	}

	isIOS() {
		if (platform
			&& platform.os
			&& platform.os.family
			&& platform.os.family.toLowerCase() === 'ios'
			&& platform.product
			&& platform.product.toLowerCase() === 'iphone') {
			return true;
		}
		// detect iphone when default desktop setting is on
		let width = screen.availWidth;
		if (screen.availHeight < screen.availWidth) {
			width = screen.availHeight;
		}
		return (this.isMacOS()
			&& this.isTouchDevice()
			&& ((width * devicePixelRatio) < 1300)
			&& ((width * devicePixelRatio) != 768));
	}

	isAndroid() {
		return platform && platform.os && platform.os.family && platform.os.family.toLowerCase() === 'android';
	}

	getBrowser() {
		if (this.isFirefox()) {
			return 'firefox';
		}
		if (this.isEdge()) {
			return 'edge';
		}
		if (this.isSafari()) {
			return 'safari';
		}
		if (this.isChrome()) {
			return 'chrome';
		}
		if (this.isInternetExplorer()) {
			return 'ie';
		}
		return null;
	}

	isMacOS() {
		if (platform && platform.os && platform.os.family) {
			return platform.os.family.toLowerCase().includes('os x');
		}
		if (navigator.userAgent.toLowerCase().match('mac os x')) {
			return true;
		}
		return false;
	}

	isNotMacOrWin() {
		return !this.isMacOS() && !this.isWin();
	}

	/* Currently, there appears to be no foolproof way to detect this, but we'll get as close as we can */
	is64bitWindows() {
		if (window.navigator.cpuClass && window.navigator.cpuClass === 'x64') {
			return true;
		}
		if (window.navigator.platform.toLowerCase().indexOf('win64') >= 0) {
			return true;
		}
		return (navigator.userAgent.match(/wow64|win64/i) !== null);
	}

	is64bitMac() {
		if (window.navigator.cpuClass && window.navigator.cpuClass === 'x64') {
			return true;
		}
		if (window.navigator.platform.toLowerCase() === 'macintel') {
			return true;
		}

		// unable to parse info from platform lib and navigator object, so rely on useragent string...
		return (window.cchome.util.compareVersionNumbers(platform.os.version, '10.7.0') === 1);
	}

	getMacVersion() {
		const agent = navigator.userAgent.toLowerCase();
		// The agent string will contain info in a format like: "mac os x 10_7_4" or "mac os x 10_8"
		if (agent.match('mac os x')) {
			let versionMatch = agent.match(/mac os x (10[_|\.][0-9]+[_|\.][0-9]+)/); // eslint-disable-line no-useless-escape
			// let versionChar = '_';
			let version;

			if (!versionMatch || versionMatch.length === 0) {
				versionMatch = agent.match(/mac os x (10[_|\.][0-9]+)/); // eslint-disable-line no-useless-escape
				if (versionMatch instanceof Array) {
					versionMatch[1] += '.0';
				}
			}

			if (versionMatch && versionMatch.length > 0) {
				version = versionMatch[1];
				// versionChar = version.match(/10\./) ? '.' : '_';
				version = version.replace(/_/g, '.');
			}
			return version;
		}
		return null;
	}

	isDeprecatedWinOS() {
		const agent = navigator.userAgent.toLowerCase();
		const depricated = agent.match(('Windows NT 5.2').toLowerCase()) || agent.match(('Windows NT 5.1').toLowerCase()) || agent.match(('Windows NT 5.0').toLowerCase()) || agent.match(('Windows NT 6.0').toLowerCase());
		return (depricated && depricated.length > 0);
	}

	isDeprecatedMacOS() {
		const macOS = this.getMacVersion();
		if (macOS && macOS !== null) {
			const macOSArr = macOS.split('.');
			return (parseInt(macOSArr[0], 10) < 10 || (macOSArr[0] === '10' && macOSArr[1] && parseInt(macOSArr[1], 10) < 11));
		}
		return false;
	}

	isNUJDeprecatedOS() {
		/**
		 * This check is for the OSes mentioned in https://wiki.corp.adobe.com/display/CCEE/M1+Learnings+-+All+Apps+App+Picker
		 * For these OSes we show a special screen
		 */
		if (this.isWin()) {
			return this.isWin7or8();
		}
		else if (this.isMacOS()) {
			const macOS = this.getMacVersion();
			if (macOS && macOS !== null) {
				const macOSArr = macOS.split('.');
				return (macOSArr[0] === '10' && macOSArr[1] && macOSArr[1] === '11')
					|| (parseInt(macOSArr[0], 10) < 10 || (macOSArr[0] === '10' && macOSArr[1] && parseInt(macOSArr[1], 10) < 11));
			}
		}
		return (this.isLinux() || this.isChromeOS());
	}
	
	isWin7or8() {
		if (this.isWin()) {
			return platform.os.version === '8' || platform.os.version === '8.1' || platform.os.version === '7';
		}
		return false;
	}

	getOSName() {
		if (this.isWin()) {
			return 'Windows';
		}
		else if (this.isChromeOS()) {
			return 'ChromeOS';
		}
		else if (this.isAndroid() || (this.isLinux() && this.isTouchDevice())) {
			return 'Android';
		}
		else if (this.isLinux() && !this.isTouchDevice()) {
			return 'Linux';
		}
		else if (this.isMacOS() && !this.isTouchDevice()) {
			return 'MacOS';
		}
		else if (this.isIOS()) {
			return 'IOS';
		}
		else if (this.isIPadOS()) {
			return 'IPadOS';
		}
		else {
			return window.navigator.platform;
		};
	}
}

export default new DeviceDetectionUtil();
